import type { Column } from '@shared/types/data-table'
import type { Fee } from '@features/billing-admin/types/fee.types'
import type { ColumnManagementConfig } from '@shared/lib/tables/utils/column-management'
import {
  createCustomColumn,
  createDateColumn,
  createIdColumn,
  createTextColumn,
} from './column-factories'
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge'
import { Badge } from '@shared/components/ui/badge'
import { formatDateOnly } from '@shared/utils/date'

export const FEE_COLUMNS: Record<string, Column<Fee>> = {
  id: createIdColumn<Fee>(),
  name: createCustomColumn<Fee>(
    'name',
    'Name',
    200,
    (fee: Fee) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">{fee.name}</span>
        <span className="text-xs text-muted-foreground font-mono">{fee.feeId}</span>
      </div>
    )
  ),
  status: createCustomColumn<Fee>(
    'status',
    'Status',
    120,
    (fee: Fee) => (
      <AppStatusBadge entityType="fee" status={fee.status || 'unknown'} />
    )
  ),
  feeType: createCustomColumn<Fee>(
    'feeType',
    'Fee Type',
    120,
    (fee: Fee) => (
      <Badge variant="outline" className="capitalize">{fee.feeType}</Badge>
    )
  ),
  description: createCustomColumn<Fee>(
    'description',
    'Description',
    240,
    (fee: Fee) => (
      <span className="text-sm text-muted-foreground truncate block max-w-[220px]">
        {fee.description || '\u2014'}
      </span>
    )
  ),
  tariffs: createCustomColumn<Fee>(
    'tariffs',
    'Tariffs',
    180,
    (fee: Fee) => (
      <span className="text-sm text-muted-foreground">
        {fee.tariffs?.length ? fee.tariffs.join(', ') : '\u2014'}
      </span>
    )
  ),
  createdDate: createDateColumn<Fee>(
    'createdDate' as keyof Fee,
    'Created',
    140,
    false
  ),
  effectiveDate: createCustomColumn<Fee>(
    'effectiveDate',
    'Effective',
    150,
    (fee: Fee) => {
      if (fee.effectiveDate) {
        return <span className="text-sm text-muted-foreground tabular-nums">{formatDateOnly(fee.effectiveDate)}</span>
      }
      if (fee.scheduledEffectiveDate) {
        return (
          <span className="text-sm text-muted-foreground tabular-nums">
            <span className="text-xs text-muted-foreground/70">Scheduled: </span>
            {formatDateOnly(fee.scheduledEffectiveDate)}
          </span>
        )
      }
      return <span className="text-sm text-muted-foreground">{'\u2014'}</span>
    }
  ),
  updatedDate: createDateColumn<Fee>(
    'updatedDate' as keyof Fee,
    'Updated',
    140,
    false
  ),
}

const FEE_COLUMN_ORDER = ['id', 'name', 'status', 'feeType', 'description', 'tariffs', 'createdDate', 'effectiveDate', 'updatedDate'] as const

const FEE_STATUS_CONFIG: Record<string, readonly string[]> = {
  all: FEE_COLUMN_ORDER,
  active: FEE_COLUMN_ORDER,
  draft: FEE_COLUMN_ORDER,
  archived: FEE_COLUMN_ORDER,
}

export const getFeeColumns = (status: string = 'all'): Column<Fee>[] => {
  const keys =
    FEE_STATUS_CONFIG[status as keyof typeof FEE_STATUS_CONFIG] ||
    FEE_STATUS_CONFIG.all
  return keys.map((key) => FEE_COLUMNS[key]).filter(Boolean)
}

export const getFeeColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(FEE_STATUS_CONFIG[status as keyof typeof FEE_STATUS_CONFIG] || FEE_STATUS_CONFIG.all)
  return Object.entries(FEE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['name', 'status'].includes(key),
    order: index
  }))
}
